// Offline cache of the Set Planner's saved sets (WhatsNextTab). The cloud
// (user-library-service) is the source of truth once authenticated; this
// localStorage mirror exists for offline use and instant hydrate on mount.
//
// The cache is USER-SCOPED data under a global key, so it must be wiped on
// logout — otherwise the next account on this device briefly sees the
// previous user's sets while the planner mounts, before the cloud list
// replaces them.

export const SAVED_SETS_KEY = 'whatsNext:savedSets';

// One-time marker: local sets have been uploaded to the cloud. After this,
// localStorage is only a mirror/offline cache of the server list — never a
// source to re-upload from (which would resurrect sets deleted on another
// device).
export const SETS_MIGRATED_KEY = 'whatsNext:savedSetsMigratedToCloud';

export const loadSavedSets = () => {
    try {
        const raw = localStorage.getItem(SAVED_SETS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};

export const persistSavedSets = (sets) => {
    localStorage.setItem(SAVED_SETS_KEY, JSON.stringify(sets));
};

// How many sets each account was last KNOWN to have, keyed by user id.
//
// The Recent row's loading skeleton may only promise cards we have evidence
// for: an empty mirror means "nothing cached", which is equally true for a
// user with nine sets on another machine and for a brand-new user with none.
// Ghost cards that resolve to an empty row are a lie, so the skeleton is shown
// only when this says the account has sets, and it renders exactly that many.
//
// Deliberately NOT wiped on logout: it is keyed by user id (the canonical
// alternative to wiping), and surviving logout is what makes the skeleton work
// on the sign-out/sign-in path, where the mirror is gone but the sets are not.
// It holds a count, never set contents.
export const SET_COUNT_KEY = 'whatsNext:setCountByUser';

const readCounts = () => {
    try { return JSON.parse(localStorage.getItem(SET_COUNT_KEY) || '{}') || {}; }
    catch { return {}; }
};

/** Integer count, or null when this device has never seen the account load. */
export const loadKnownSetCount = (userId) => {
    if (!userId) return null;
    const n = readCounts()[userId];
    return Number.isInteger(n) && n >= 0 ? n : null;
};

export const rememberSetCount = (userId, count) => {
    if (!userId || !Number.isInteger(count) || count < 0) return;
    try {
        localStorage.setItem(SET_COUNT_KEY, JSON.stringify({ ...readCounts(), [userId]: count }));
    } catch { /* private mode */ }
};

export const clearPlannerSetsCache = () => {
    try {
        localStorage.removeItem(SAVED_SETS_KEY);
        localStorage.removeItem(SETS_MIGRATED_KEY);
    } catch { /* private mode */ }
};
